import React, { memo, useCallback } from "react";
import { truncatePartyNames } from "../TruncateParties";

const parseFancyDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const cleanedDate = dateStr.replace(/(\d+)(st|nd|rd|th)/, "$1");
    const parsed = new Date(cleanedDate);
    if (isNaN(parsed.getTime())) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const checkIsUndated = (caseItem) => {
  const rawDate = (
    caseItem.caseStatus?.["Next Hearing Date"] ||
    caseItem.customUndatedRows?.["Next Hearing Date"] ||
    ""
  )
    .toString()
    .trim();

  if (
    !rawDate ||
    ["", "-", "--", "---", "NA", "N/A", ":", ":-"].includes(rawDate)
  ) {
    return false;
  }

  const parsed = parseFancyDate(rawDate);
  if (!parsed) return true;

  const today = new Date();
  const parsedDateOnly = new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate()
  );
  const todayDateOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return parsedDateOnly < todayDateOnly;
};

const ManageCaseRow = ({
  caseItem,
  isChecked,
  isFocused,
  onCheck,
  index,
  caseRefs, // Receiving the ref object from parent
}) => {
  const isAssigned = caseItem.assignedTo && caseItem.assignedTo.length > 0;
  const isUndated = checkIsUndated(caseItem);

  const statusText = isUndated
    ? "N/A"
    : caseItem.caseStatus?.["Case Status"] || "Pending";

  // FIX 3: Robust Ref Handler
  // This ensures the parent's caseRefs array is updated correctly
  const setRowRef = useCallback(
    (element) => {
      if (caseRefs && caseRefs.current) {
        caseRefs.current[index] = element;
      }
    },
    [caseRefs, index]
  );

  const handleClick = useCallback(() => {
    if (!isAssigned) {
      onCheck(caseItem);
    }
  }, [isAssigned, onCheck, caseItem]);

  const handleCheckboxChange = useCallback(
    (e) => {
      e.stopPropagation();
      if (!isAssigned) {
        onCheck(caseItem);
      }
    },
    [isAssigned, onCheck, caseItem]
  );

  return (
    <div
      className={`manage-case-card ${isFocused ? "focused" : ""} ${
        isAssigned ? "assigned" : ""
      }`}
      ref={setRowRef}
      onClick={handleClick}
      style={{ cursor: isAssigned ? "not-allowed" : "pointer" }}
      title={
        isAssigned
          ? `Assigned to: ${caseItem.assignedTo.map((u) => u.name).join(", ")}`
          : ""
      }
    >
      <div
        className="manage-case-checkbox"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleCheckboxChange}
          disabled={isAssigned}
          className="case-checkbox"
          title={isAssigned ? "Case is already assigned" : ""}
        />
      </div>

      <div className="manage-case-details">
        <div className="manage-case-main-info">
          <div className="manage-case-parties">
            {truncatePartyNames(caseItem?.parties)}
          </div>

          <div className="manage-case-secondary-info">
            <div className="manage-case-info-item">
              <span className="manage-case-label">CNR:</span>
              <span>{caseItem.viewDetails?.courtRef || "N/A"}</span>
            </div>

            <div className="manage-case-info-item">
              <span className="manage-case-label">Case No:</span>
              <span>{caseItem.caseTypeAndNumber || "N/A"}</span>
            </div>

            <div className="manage-case-info-item">
              <span className="manage-case-label">Court:</span>
              <span>{caseItem.courtName || "N/A"}</span>
            </div>
          </div>
        </div>

        <div className="manage-case-right-info">
          <div className="manage-case-status">
            <span
              className={`manage-case-status-badge ${
                isUndated
                  ? "na"
                  : caseItem.caseStatus?.["Case Status"]
                      ?.toLowerCase()
                      .includes("disposed")
                  ? "disposed"
                  : "pending"
              }`}
            >
              {statusText}
            </span>
          </div>

          <div className="manage-case-date">
            <span className="manage-case-label">Filed:</span>
            <span>{caseItem.caseDetails?.["Filing Date"] || "N/A"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

ManageCaseRow.displayName = "ManageCaseRow";

// FIX 4: REMOVED CUSTOM COMPARISON
// We use default React.memo(). 
// Since 'caseItem' is a new object every time data is fetched, 
// this ensures the row updates correctly when data (like assignment) changes.
export default memo(ManageCaseRow);